"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { Search, TrendingUp, User, Hash, FileText, Filter } from "lucide-react"
import PostCard from "./PostCard"
import { useAuth } from "../context/AuthContext"
import { useNavigate } from "react-router-dom"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`

const SearchHeader = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
`

const SearchInputContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s;
  
  &:focus {
    border-color: #2563eb;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
`

const SearchFilters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  background-color: ${props => props.$active ? '#2563eb' : 'white'};
  color: ${props => props.$active ? 'white' : '#6b7280'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #2563eb;
    color: ${props => props.$active ? 'white' : '#2563eb'};
  }
`

const TrendingSection = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
`

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const TrendingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TrendingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f9fafb;
  }
`

const TrendingContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const TrendingIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: 600;
`

const TrendingText = styled.div`
  display: flex;
  flex-direction: column;
`

const TrendingName = styled.div`
  font-weight: 500;
  color: #111827;
`

const TrendingCount = styled.div`
  font-size: 12px;
  color: #6b7280;
`

const SearchResults = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
`

const ResultsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`

const ResultsCount = styled.div`
  font-size: 14px;
  color: #6b7280;
`

const UserResult = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-1px);
  }
`

const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #e5e7eb;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const UserInfo = styled.div`
  flex: 1;
`

const UserName = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
`

const UserStats = styled.div`
  font-size: 12px;
  color: #6b7280;
  display: flex;
  gap: 12px;
`

const FollowButton = styled.button`
  padding: 6px 16px;
  border: 1px solid #2563eb;
  border-radius: 6px;
  background-color: ${props => props.$following ? '#2563eb' : 'white'};
  color: ${props => props.$following ? 'white' : '#2563eb'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #2563eb;
    color: white;
  }
`

const HashtagResult = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-1px);
  }
`

const HashtagInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const HashtagIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const HashtagText = styled.div`
  display: flex;
  flex-direction: column;
`

const HashtagName = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 2px;
`

const HashtagUsage = styled.div`
  font-size: 12px;
  color: #6b7280;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 20px;
  color: #6b7280;
`

export default function SearchPage() {
  const { isLoggedIn } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const [activeFilter, setActiveFilter] = useState("all")
  const [loading, setLoading] = useState(false)
  const [searchResults, setSearchResults] = useState({
    posts: { items: [], total: 0 },
    users: { items: [], total: 0 },
    hashtags: { items: [], total: 0 }
  })
  const [trendingData, setTrendingData] = useState([])
  const [searchHistory, setSearchHistory] = useState([])

  // 트렌딩 데이터 로드
  useEffect(() => {
    fetchTrending()
  }, [])

  const fetchTrending = async () => {
    try {
      const response = await fetch('/search/trending?limit=5', {
        credentials: 'include'
      })
      
      const data = await response.json()
      if (data.success) {
        setTrendingData(data.trending)
      }
    } catch (error) {
      console.error('트렌딩 데이터 로드 오류:', error)
    }
  }

  // 검색 실행
  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults({
        posts: { items: [], total: 0 },
        users: { items: [], total: 0 },
        hashtags: { items: [], total: 0 }
      })
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/search/all?q=${encodeURIComponent(query)}&limit=10`, {
        credentials: 'include'
      })
      
      const data = await response.json()
      if (data.success) {
        setSearchResults(data.results)
        
        // 검색 기록 저장
        const newHistory = [query, ...searchHistory.filter(h => h !== query)].slice(0, 10)
        setSearchHistory(newHistory)
        localStorage.setItem('searchHistory', JSON.stringify(newHistory))
      }
    } catch (error) {
      console.error('검색 오류:', error)
    } finally {
      setLoading(false)
    }
  }

  // 검색어 변경 핸들러
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    
    // 디바운싱
    const timeoutId = setTimeout(() => {
      performSearch(value)
    }, 500)
    
    return () => clearTimeout(timeoutId)
  }

  // 트렌딩 해시태그 클릭
  const handleTrendingClick = (name) => {
    setSearchQuery(`#${name}`)
    performSearch(`#${name}`)
  }

  // 필터된 결과 가져오기
  const getFilteredResults = () => {
    switch (activeFilter) {
      case 'posts':
        return { items: searchResults.posts.items, total: searchResults.posts.total, type: 'posts' }
      case 'users':
        return { items: searchResults.users.items, total: searchResults.users.total, type: 'users' }
      case 'hashtags':
        return { items: searchResults.hashtags.items, total: searchResults.hashtags.total, type: 'hashtags' }
      default:
        return { 
          items: [
            ...searchResults.posts.items,
            ...searchResults.users.items,
            ...searchResults.hashtags.items
          ], 
          total: searchResults.posts.total + searchResults.users.total + searchResults.hashtags.total,
          type: 'all'
        }
    }
  }

  const filteredResults = getFilteredResults()

  return (
    <Container>
      {/* 검색 헤더 */}
      <SearchHeader>
        <SearchInputContainer>
          <SearchIcon>
            <Search size={20} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="게시물, 사용자, 해시태그 검색..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </SearchInputContainer>
        
        <SearchFilters>
          <FilterButton 
            $active={activeFilter === 'all'} 
            onClick={() => setActiveFilter('all')}
          >
            <Filter size={14} />
            전체
          </FilterButton>
          <FilterButton 
            $active={activeFilter === 'posts'} 
            onClick={() => setActiveFilter('posts')}
          >
            <FileText size={14} />
            게시물 ({searchResults.posts.total})
          </FilterButton>
          <FilterButton 
            $active={activeFilter === 'users'} 
            onClick={() => setActiveFilter('users')}
          >
            <User size={14} />
            사용자 ({searchResults.users.total})
          </FilterButton>
          <FilterButton 
            $active={activeFilter === 'hashtags'} 
            onClick={() => setActiveFilter('hashtags')}
          >
            <Hash size={14} />
            해시태그 ({searchResults.hashtags.total})
          </FilterButton>
        </SearchFilters>
      </SearchHeader>

      {/* 검색 결과가 없을 때 트렌딩 표시 */}
      {!searchQuery && trendingData.length > 0 && (
        <TrendingSection>
          <SectionTitle>
            <TrendingUp size={20} />
            인기 해시태그
          </SectionTitle>
          <TrendingList>
            {trendingData.map((item, index) => (
              <TrendingItem key={index} onClick={() => handleTrendingClick(item.name)}>
                <TrendingContent>
                  <TrendingIcon>{index + 1}</TrendingIcon>
                  <TrendingText>
                    <TrendingName>#{item.name}</TrendingName>
                    <TrendingCount>{item.recent_usage}개 게시물</TrendingCount>
                  </TrendingText>
                </TrendingContent>
              </TrendingItem>
            ))}
          </TrendingList>
        </TrendingSection>
      )}

      {/* 검색 결과 */}
      {searchQuery && (
        <SearchResults>
          <ResultsHeader>
            <ResultsTitle>검색 결과</ResultsTitle>
            <ResultsCount>{filteredResults.total}개 결과</ResultsCount>
          </ResultsHeader>

          {loading ? (
            <LoadingState>검색 중...</LoadingState>
          ) : filteredResults.items.length === 0 ? (
            <EmptyState>
              <p>"{searchQuery}"에 대한 검색 결과가 없습니다.</p>
              <p>다른 키워드로 검색해 보세요.</p>
            </EmptyState>
          ) : (
            filteredResults.items.map((item, index) => {
              // 게시물 결과
              if (item.type === 'post') {
                return (
                  <PostCard
                    key={`post-${item.id}`}
                    post={{
                      id: item.id,
                      author: item.author_username,
                      authorAvatar: item.author_avatar,
                      timestamp: item.created_at,
                      images: item.images,
                      tags: item.hashtags || [],
                      description: item.content,
                      like_count: item.like_count,
                      comment_count: item.comment_count
                    }}
                    onPostDeleted={() => performSearch(searchQuery)}
                    onPostUpdated={() => performSearch(searchQuery)}
                  />
                )
              }
              
              // 사용자 결과
              if (item.type === 'user') {
                return (
                  <UserResult key={`user-${item.id}`} onClick={() => navigate(`/profile/${item.username}`)}>
                    <UserAvatar>
                      {item.avatar_url ? (
                        <img src={item.avatar_url} alt={item.username} />
                      ) : (
                        <User size={24} />
                      )}
                    </UserAvatar>
                    <UserInfo>
                      <UserName>@{item.username}</UserName>
                      {item.full_name && <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>{item.full_name}</div>}
                      <UserStats>
                        <span>팔로워 {item.follower_count || 0}</span>
                        <span>게시물 {item.post_count || 0}</span>
                      </UserStats>
                    </UserInfo>
                    {isLoggedIn && (
                      <FollowButton>팔로우</FollowButton>
                    )}
                  </UserResult>
                )
              }
              
              // 해시태그 결과
              if (item.type === 'hashtag') {
                return (
                  <HashtagResult key={`hashtag-${item.id}`} onClick={() => handleTrendingClick(item.name)}>
                    <HashtagInfo>
                      <HashtagIcon>
                        <Hash size={20} />
                      </HashtagIcon>
                      <HashtagText>
                        <HashtagName>#{item.name}</HashtagName>
                        <HashtagUsage>{item.usage_count}개 게시물</HashtagUsage>
                      </HashtagText>
                    </HashtagInfo>
                  </HashtagResult>
                )
              }
              
              return null
            })
          )}
        </SearchResults>
      )}
    </Container>
  )
}